/**
 * PATH: packages/email-hub/src/feature/lib/parseEmailSearchQuery.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-PARSEEMAILSEARCHQUERY-001
 * PURPOSE: Pure parser that turns a Gmail-style search input
 *          ("from:mark has:attachment invoice") into:
 *            - free-text portion ("invoice"), passed to the BE's
 *              existing `?search=` ICONTAINS/FTS path
 *            - structured operator values that map 1:1 onto the BE
 *              filter params the EmailListView already honours
 *              (`from_address__icontains`, `has_attachments`,
 *              `has_calendar_event`, `received_at__gte/lte`,
 *              `is_read`, `is_starred`).
 *
 * WHY:     Operator request 2026-05-19 — operator typed a name into
 *          the search box and the inbox didn't surface example
 *          searches like `from:`. The BE already supports rich
 *          filtering; the FE just needed to learn Gmail's vocabulary
 *          so the search box becomes discoverable.
 *
 *          Pure I→O (no React, no router, no globals) so the
 *          parser tests run as plain unit tests and the
 *          EmailSearchSuggestions popover + InboxPage's debounced
 *          filter-projection effect can call it in lockstep
 *          without duplicating the grammar.
 *
 * Grammar (informal):
 *
 *   QUERY     := (TOKEN | WORD)*
 *   TOKEN     := KEY ':' VALUE                          // operator
 *   WORD      := non-whitespace chunk (not a TOKEN)     // free text
 *   VALUE     := QUOTED | BAREWORD
 *   QUOTED    := '"' ... '"'                            // multi-word
 *   BAREWORD  := non-whitespace, non-quote chunk
 *
 *   KEY       := one of EMAIL_SEARCH_OPERATORS keys
 *                (other `foo:bar` patterns fall through to free text
 *                so a user pasting a URL like "https://..." doesn't
 *                trip the parser).
 *
 * Examples (see test for the full table):
 *
 *   parseEmailSearchQuery('abhishek')
 *     → { freeText: 'abhishek' }
 *
 *   parseEmailSearchQuery('from:mark invoice')
 *     → { freeText: 'invoice', fromAddressContains: 'mark' }
 *
 *   parseEmailSearchQuery('from:"mark newman" has:attachment is:unread')
 *     → { freeText: '',
 *         fromAddressContains: 'mark newman',
 *         hasAttachments: true,
 *         isReadFilter: false }
 *
 *   parseEmailSearchQuery('before:2026-05-01 after:2026-04-01')
 *     → { freeText: '',
 *         receivedAtLte: '2026-05-01T23:59:59Z',
 *         receivedAtGte: '2026-04-01T00:00:00Z' }
 */

/** The structured filter values the parser emits. Names match the
 *  InboxPage state vars so the wiring is a 1:1 spread. ``undefined``
 *  (not just falsy) means "the user didn't type this operator" — so
 *  the sidebar's explicit value is preserved. */
export interface ParsedEmailSearch {
  /** Free-text portion (everything that wasn't an operator). The
   *  empty string is meaningful: it tells the consumer to CLEAR
   *  any existing free-text filter, not to leave it alone. */
  freeText: string
  fromAddressContains?: string
  hasAttachments?: boolean
  hasCalendarEvent?: boolean
  isReadFilter?: boolean
  isStarredFilter?: boolean
  /** ISO-8601 UTC strings — start of day for `after:`, end of day
   *  for `before:`. The BE filter is `received_at__gte / __lte` so
   *  these round to the inclusive boundary the operator expects
   *  ("after:2026-04-01" = "anything received on or after the
   *  first second of April 1"). */
  receivedAtGte?: string
  receivedAtLte?: string
  /** `filename:<term>` — maps to the BE `attachment_filename` param
   *  (fe/be G1 phase 2): a dedicated trigram-indexed narrow on
   *  attachment filenames, independent of the free-text search. */
  attachmentFilename?: string
  /** `in:attachment` — maps to the BE `search_scope=attachment`
   *  param: the free-text search matches ONLY the attachment legs
   *  (filename + extracted document content). */
  searchScope?: 'attachment'
  /** Echoes the raw tokens so the suggestion popover can render
   *  "you typed: from=mark, has=attachment" affordances. The order
   *  is the order the tokens appeared in the input. */
  recognisedOperators: ParsedOperatorToken[]
}

export interface ParsedOperatorToken {
  /** The key as the operator dictionary spells it (lowercased). */
  key: EmailSearchOperatorKey
  /** The raw value as typed. For booleans this is e.g.
   *  `'attachment'` (for `has:attachment`); the boolean result
   *  is stored on the matching ParsedEmailSearch field. */
  rawValue: string
}

/** The operator dictionary. Lower-cased keys; aliases (e.g.
 *  `has:attachments` for `has:attachment`) share an entry via the
 *  ``aliases`` field so the suggestion popover only has one row
 *  per concept but the parser accepts both. */
export const EMAIL_SEARCH_OPERATORS = {
  from: {
    label: 'from:',
    example: 'from:mark',
    description: 'Match emails whose sender (name or address) contains this text.',
  },
  to: {
    label: 'to:',
    example: 'to:accounts',
    description: 'Match emails whose recipients (to/cc) contain this text.',
  },
  subject: {
    label: 'subject:',
    example: 'subject:invoice',
    description: 'Match emails whose subject contains this text.',
  },
  filename: {
    label: 'filename:',
    example: 'filename:quote.pdf',
    description: 'Match attachment filenames (implies has:attachment).',
  },
  in: {
    label: 'in:',
    example: 'in:attachment',
    description: 'Scope the search — in:attachment searches inside attached documents only.',
    valueSuggestions: ['attachment'],
  },
  has: {
    label: 'has:',
    example: 'has:attachment',
    description: 'Filter by attachment / calendar invite presence.',
    valueSuggestions: ['attachment', 'calendar'],
  },
  is: {
    label: 'is:',
    example: 'is:unread',
    description: 'Filter by read state / star / archive.',
    valueSuggestions: ['unread', 'read', 'starred'],
  },
  after: {
    label: 'after:',
    example: 'after:2026-04-01',
    description: 'Emails received ON OR AFTER this date (YYYY-MM-DD).',
  },
  before: {
    label: 'before:',
    example: 'before:2026-05-01',
    description: 'Emails received ON OR BEFORE this date (YYYY-MM-DD).',
  },
} as const

export type EmailSearchOperatorKey = keyof typeof EMAIL_SEARCH_OPERATORS

const OPERATOR_KEYS = Object.keys(EMAIL_SEARCH_OPERATORS) as EmailSearchOperatorKey[]

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** Split the raw input into tokens, respecting double-quoted spans.
 *  Returns the array of raw tokens (no operator parsing yet). The
 *  ``hasTrailingSpace`` flag is exported so the suggestion popover
 *  can decide whether to autocomplete the LAST token in place
 *  (no trailing space) or to suggest a fresh operator (trailing
 *  space → user is about to type the next token). */
export interface TokenizedSearch {
  tokens: string[]
  hasTrailingSpace: boolean
}

export function tokenizeEmailSearch(input: string): TokenizedSearch {
  const tokens: string[] = []
  let buf = ''
  let inQuotes = false
  for (const ch of input) {
    if (ch === '"') {
      inQuotes = !inQuotes
      buf += ch
      continue
    }
    if (!inQuotes && /\s/.test(ch)) {
      if (buf !== '') {
        tokens.push(buf)
        buf = ''
      }
      continue
    }
    buf += ch
  }
  if (buf !== '') tokens.push(buf)
  return {
    tokens,
    hasTrailingSpace: input.length > 0 && /\s$/.test(input),
  }
}

/** Strip wrapping double quotes so `from:"mark newman"` parses as
 *  the value `mark newman` (and `from:mark` stays `mark`). */
function stripQuotes(value: string): string {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    return value.slice(1, -1)
  }
  return value
}

/** Parse a single token. Returns `null` if the token isn't a
 *  recognised operator (the caller treats it as free text). */
function parseToken(token: string): ParsedOperatorToken | null {
  const colonIdx = token.indexOf(':')
  if (colonIdx <= 0 || colonIdx === token.length - 1) {
    return null
  }
  const rawKey = token.slice(0, colonIdx).toLowerCase()
  if (!OPERATOR_KEYS.includes(rawKey as EmailSearchOperatorKey)) {
    return null
  }
  const rawValue = stripQuotes(token.slice(colonIdx + 1))
  if (rawValue === '') return null
  return {
    key: rawKey as EmailSearchOperatorKey,
    rawValue,
  }
}

/** Project a single recognised operator onto the structured filter
 *  fields. Multiple operators of the same key collapse to the last
 *  one (matches Gmail's behaviour — `from:a from:b` searches for
 *  emails from `b`; mostly relevant when the suggestion popover
 *  appends a fresh `from:` while one already exists).
 *
 *  Returns an optional FREE-TEXT contribution: `to:` and `subject:`
 *  have no dedicated BE param — the shared `?search=` matcher already
 *  covers subject and to/cc recipients — so their values ride the
 *  search param. `filename:` maps to the dedicated
 *  `attachment_filename` BE param (G1 phase 2, be!4489) and pins
 *  `has_attachments=true`; `in:attachment` maps to
 *  `search_scope=attachment`. */
function applyToken(
  out: ParsedEmailSearch,
  token: ParsedOperatorToken,
): string | undefined {
  const value = token.rawValue
  switch (token.key) {
    case 'from':
      out.fromAddressContains = value
      return
    case 'to':
    case 'subject':
      return value
    case 'filename':
      // G1 phase 2 (be!4489): dedicated BE param — no longer rides the
      // shared `?search=`. Still pins has_attachments so the filter
      // pill affordances stay consistent.
      out.hasAttachments = true
      out.attachmentFilename = value
      return
    case 'in': {
      const v = value.toLowerCase()
      if (v === 'attachment' || v === 'attachments') {
        out.searchScope = 'attachment'
      }
      return
    }
    case 'has': {
      const v = value.toLowerCase()
      if (v === 'attachment' || v === 'attachments') {
        out.hasAttachments = true
      } else if (v === 'calendar' || v === 'invite' || v === 'event') {
        out.hasCalendarEvent = true
      }
      return
    }
    case 'is': {
      const v = value.toLowerCase()
      if (v === 'read') out.isReadFilter = true
      else if (v === 'unread') out.isReadFilter = false
      else if (v === 'starred' || v === 'star') out.isStarredFilter = true
      return
    }
    case 'after': {
      // YYYY-MM-DD → ISO-8601 start-of-day UTC. The user typing a
      // bare date almost certainly means "anything from that
      // calendar day onwards" — round to 00:00:00Z so the BE
      // `received_at__gte` is inclusive of the whole day.
      if (ISO_DATE_RE.test(value)) {
        out.receivedAtGte = `${value}T00:00:00Z`
      }
      return
    }
    case 'before': {
      // Symmetric: end-of-day UTC so `before:2026-05-01` includes
      // anything received on May 1st (matches Gmail's semantics).
      if (ISO_DATE_RE.test(value)) {
        out.receivedAtLte = `${value}T23:59:59Z`
      }
      return
    }
  }
}

/** Main entry point. Pure function. */
export function parseEmailSearchQuery(input: string): ParsedEmailSearch {
  const out: ParsedEmailSearch = {
    freeText: '',
    recognisedOperators: [],
  }
  if (!input || input.trim() === '') {
    return out
  }
  const { tokens } = tokenizeEmailSearch(input)
  const freeTextParts: string[] = []
  for (const token of tokens) {
    const parsed = parseToken(token)
    if (parsed === null) {
      freeTextParts.push(stripQuotes(token))
      continue
    }
    out.recognisedOperators.push(parsed)
    const freeTextContribution = applyToken(out, parsed)
    if (freeTextContribution !== undefined) {
      freeTextParts.push(freeTextContribution)
    }
  }
  out.freeText = freeTextParts.join(' ').trim()
  return out
}

/** Suggestion-popover helper: given the current input + cursor
 *  position, return either:
 *   - the list of operator keys to surface (when the cursor is in a
 *     fresh token or at the very start), OR
 *   - the list of value suggestions for a partially-typed operator
 *     (e.g. cursor after `is:` shows ["unread", "read", "starred"]).
 *
 *  Designed to be called on every keystroke in the search input —
 *  cheap (no regex backtracking, no allocs beyond the result). */
export interface SearchSuggestion {
  /** What the suggestion inserts into the input. */
  insertText: string
  /** What the popover shows. */
  label: string
  /** One-line help text shown below the label. */
  description?: string
  /** When set, the suggestion REPLACES the current token rather than
   *  being appended (used for value-suggestions like `is:unread`
   *  that overwrite a partial `is:u`). */
  replaceCurrentToken?: boolean
}

export function suggestEmailSearchTokens(input: string): SearchSuggestion[] {
  // Empty input → show every operator with its example.
  if (!input || input.trim() === '') {
    return OPERATOR_KEYS.map((k) => ({
      insertText: EMAIL_SEARCH_OPERATORS[k].label,
      label: EMAIL_SEARCH_OPERATORS[k].label,
      description: EMAIL_SEARCH_OPERATORS[k].description,
    }))
  }

  const { tokens, hasTrailingSpace } = tokenizeEmailSearch(input)
  if (tokens.length === 0) {
    return []
  }
  // If we're at the start of a fresh token (trailing whitespace),
  // suggest operators that aren't already in the input.
  if (hasTrailingSpace) {
    const usedKeys = new Set<EmailSearchOperatorKey>(
      tokens
        .map(parseToken)
        .filter((t): t is ParsedOperatorToken => t !== null)
        .map((t) => t.key),
    )
    return OPERATOR_KEYS
      .filter((k) => !usedKeys.has(k))
      .map((k) => ({
        insertText: EMAIL_SEARCH_OPERATORS[k].label,
        label: EMAIL_SEARCH_OPERATORS[k].label,
        description: EMAIL_SEARCH_OPERATORS[k].description,
      }))
  }

  // We're inside the LAST token. Two sub-cases:
  //   (a) the partial token has a `:` already → suggest values.
  //   (b) no colon → autocomplete operator keys that start with
  //       what the user has typed so far.
  const last = tokens[tokens.length - 1]!
  const colonIdx = last.indexOf(':')

  if (colonIdx > 0) {
    const key = last.slice(0, colonIdx).toLowerCase() as EmailSearchOperatorKey
    if (!OPERATOR_KEYS.includes(key)) return []
    const def = EMAIL_SEARCH_OPERATORS[key]
    const values = (def as { valueSuggestions?: readonly string[] }).valueSuggestions
    if (!values || values.length === 0) {
      // No predefined values (e.g. `from:`, `after:`, `before:`).
      // Surface the example as a hint.
      return [
        {
          insertText: def.example,
          label: def.example,
          description: def.description,
          replaceCurrentToken: true,
        },
      ]
    }
    const partial = last.slice(colonIdx + 1).toLowerCase()
    return values
      .filter((v) => v.startsWith(partial))
      .map((v) => ({
        insertText: `${def.label}${v}`,
        label: `${def.label}${v}`,
        description: def.description,
        replaceCurrentToken: true,
      }))
  }

  // (b) Operator-key autocomplete: the user typed `f` or `is` and
  // we want to surface keys that start with that prefix.
  const lower = last.toLowerCase()
  return OPERATOR_KEYS
    .filter((k) => k.startsWith(lower))
    .map((k) => ({
      insertText: EMAIL_SEARCH_OPERATORS[k].label,
      label: EMAIL_SEARCH_OPERATORS[k].label,
      description: EMAIL_SEARCH_OPERATORS[k].description,
      replaceCurrentToken: true,
    }))
}

/** Replace the last token of `input` with `insertText`. Used when
 *  the user clicks a suggestion that has `replaceCurrentToken=true`.
 *  Preserves quoted values and the trailing-space contract. */
export function applySuggestion(
  input: string,
  suggestion: SearchSuggestion,
): string {
  if (!suggestion.replaceCurrentToken) {
    // Append: add a space if the input doesn't already end in one.
    if (!input || /\s$/.test(input)) return `${input}${suggestion.insertText}`
    return `${input} ${suggestion.insertText}`
  }
  const { tokens, hasTrailingSpace } = tokenizeEmailSearch(input)
  if (tokens.length === 0 || hasTrailingSpace) {
    return `${input}${suggestion.insertText}`
  }
  // Replace the LAST token in place.
  const lastIdx = input.lastIndexOf(tokens[tokens.length - 1]!)
  return input.slice(0, lastIdx) + suggestion.insertText
}
